import React, { useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Grid, FormControl, InputLabel, Select, MenuItem, CircularProgress, FormHelperText, Box } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { COLORS, MONTH_NAMES } from '../../constants';
import MultiSelectStations from './components/MultiSelectStations';
import MultiSelectGeneric from './components/MultiSelectGeneric';

const useStyles = makeStyles((theme) => ({
    formControl: {
        width: '100%',
        '& .MuiOutlinedInput-root': {
            borderRadius: '8px',
        },
    },
    selectLabel: {
        fontWeight: 'bold',
        marginBottom: theme.spacing(0.5),
        color: COLORS.textPrimary,
        fontSize: '14px',
    },
}));


/**
 * Panel de filtros para Registros de Uso
 * Proporciona controles para filtrar por estación, año, mes, empresa, área y cargo
 * @param {Object} props - Props del componente
 * @param {Object} props.filters - Objeto con valores actuales de los filtros
 * @param {Array|string} props.filters.station - IDs de estaciones seleccionadas (array o string único)
 * @param {number} props.filters.year - Año seleccionado
 * @param {number} props.filters.month - Mes seleccionado
 * @param {string} props.filters.company - ID de la empresa seleccionada
 * @param {string} props.filters.area - ID del área seleccionada
 * @param {string} props.filters.position - ID del cargo seleccionado
 * @param {function} props.onFilterChange - Callback cuando cambia un filtro
 * @param {Array} props.stations - Array de estaciones disponibles
 * @param {Array} props.companies - Array de empresas disponibles
 * @param {Array} props.areas - Array de áreas disponibles
 * @param {Array} props.positions - Array de cargos disponibles
 * @returns {JSX.Element} Panel de filtros
 */
export default function FilterPanel({ 
    filters, 
    onFilterChange,
    stations = [],
    companies = [],
    areas = [],
    positions = [],
    areasEnCascada = [],
    positionsEnCascada = [],
    loadingFiltros = false,
    errorFiltros = null,
    onStationChange = null,
    onCloseStationSelector = null,
    onCloseCompanySelector = null,
    onCloseAreaSelector = null,
    onClosePositionSelector = null
}) {
    const classes = useStyles();

    // Generar opciones de meses desde MONTH_NAMES
    const monthOptions = useMemo(() => 
        MONTH_NAMES.map((name, index) => ({
            value: index + 1,
            label: name
        })),
        []
    );

    // Generar opciones de años (últimos 5 años)
    const yearOptions = useMemo(() => {
        const currentYear = new Date().getFullYear();
        return Array.from({ length: 5 }, (_, i) => currentYear - i);
    }, []);

    const handleChange = (field) => (event) => {
        onFilterChange(field, event.target.value);
    };

    const handleStationChange = (selectedStations) => {
        onFilterChange('station', selectedStations);
    };

    return (
        <Grid container spacing={2}>
            {/* Row 1: Station, Year, Month */}
            <Grid item xs={12} sm={4}>
                <InputLabel className={classes.selectLabel}>Estaciones</InputLabel>
                <MultiSelectStations
                    stations={stations}
                    selected={Array.isArray(filters.station) ? filters.station : (filters.station ? [filters.station] : [])}
                    onChange={handleStationChange}
                    onClose={onCloseStationSelector}
                    disabled={false}
                    loading={false}
                    placeholder="Seleccionar estaciones"
                    searchPlaceholder="Buscar estaciones..."
                />
            </Grid>

            <Grid item xs={12} sm={4}>
                <InputLabel className={classes.selectLabel}>Año</InputLabel>
                <FormControl variant="outlined" className={classes.formControl} size="small">
                    <Select
                        value={filters.year || ''}
                        onChange={handleChange('year')}
                        displayEmpty
                    >
                        <MenuItem value="">
                            <em>Seleccione un año</em>
                        </MenuItem>
                        {yearOptions.map((year) => (
                            <MenuItem key={year} value={year}>
                                {year}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Grid>

            <Grid item xs={12} sm={4}>
                <InputLabel className={classes.selectLabel}>Mes</InputLabel>
                <FormControl variant="outlined" className={classes.formControl} size="small">
                    <Select
                        value={filters.month || ''}
                        onChange={handleChange('month')}
                        displayEmpty
                    >
                        <MenuItem value="">
                            <em>Seleccione un mes</em>
                        </MenuItem>
                        {monthOptions.map((month) => (
                            <MenuItem key={month.value} value={month.value}>
                                {month.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Grid>

            {/* Row 2: Company, Area, Position */}
            <Grid item xs={12} sm={4}>
                <InputLabel className={classes.selectLabel}>Empresa</InputLabel>
                <MultiSelectGeneric
                    items={companies}
                    selected={Array.isArray(filters.company) ? filters.company : (filters.company ? [filters.company] : [])}
                    onChange={(selected) => onFilterChange('company', selected)}
                    onClose={onCloseCompanySelector ? () => {} : null}
                    displayField="label"
                    idField="id"
                    disabled={!filters.station || (Array.isArray(filters.station) && filters.station.length === 0) || loadingFiltros}
                    loading={loadingFiltros}
                    placeholder={!filters.station || (Array.isArray(filters.station) && filters.station.length === 0) ? 'Seleccione estación primero' : 'Seleccionar empresas'}
                    searchPlaceholder="Buscar empresas..."
                />
                {errorFiltros && <FormHelperText error>{errorFiltros}</FormHelperText>}
            </Grid>

            <Grid item xs={12} sm={4}>
                <InputLabel className={classes.selectLabel}>Área</InputLabel>
                <MultiSelectGeneric
                    items={areasEnCascada}
                    selected={Array.isArray(filters.area) ? filters.area : (filters.area ? [filters.area] : [])}
                    onChange={(selected) => onFilterChange('area', selected)}
                    onClose={onCloseAreaSelector ? () => {} : null}
                    displayField="label"
                    idField="id"
                    disabled={!filters.company || (Array.isArray(filters.company) && filters.company.length === 0) || loadingFiltros}
                    loading={loadingFiltros}
                    placeholder={!filters.company || (Array.isArray(filters.company) && filters.company.length === 0) ? 'Seleccione empresa primero' : 'Seleccionar áreas'}
                    searchPlaceholder="Buscar áreas..."
                />
                {errorFiltros && <FormHelperText error>{errorFiltros}</FormHelperText>}
            </Grid>

            <Grid item xs={12} sm={4}>
                <InputLabel className={classes.selectLabel}>Cargo</InputLabel>
                <MultiSelectGeneric
                    items={positionsEnCascada}
                    selected={Array.isArray(filters.position) ? filters.position : (filters.position ? [filters.position] : [])}
                    onChange={(selected) => onFilterChange('position', selected)}
                    onClose={onClosePositionSelector ? () => {} : null}
                    displayField="label"
                    idField="id"
                    disabled={!filters.area || (Array.isArray(filters.area) && filters.area.length === 0) || loadingFiltros}
                    loading={loadingFiltros}
                    placeholder={!filters.area || (Array.isArray(filters.area) && filters.area.length === 0) ? 'Seleccione área primero' : 'Seleccionar cargos'}
                    searchPlaceholder="Buscar cargos..."
                />
                {errorFiltros && <FormHelperText error>{errorFiltros}</FormHelperText>}
            </Grid>
        </Grid>
    );
}

FilterPanel.propTypes = {
    filters: PropTypes.shape({
        station: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
        year: PropTypes.number,
        month: PropTypes.number,
        company: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
        area: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
        position: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
    }).isRequired,
    onFilterChange: PropTypes.func.isRequired,
    stations: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
            name: PropTypes.string.isRequired,
        })
    ),
    companies: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
            label: PropTypes.string.isRequired,
            value: PropTypes.string,
            siguiente: PropTypes.number,
        })
    ),
    areas: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
            label: PropTypes.string.isRequired,
            value: PropTypes.string,
            siguiente: PropTypes.number,
        })
    ),
    positions: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
            label: PropTypes.string.isRequired,
            value: PropTypes.string,
            siguiente: PropTypes.number,
        })
    ),
    loadingFiltros: PropTypes.bool,
    errorFiltros: PropTypes.string,
    onStationChange: PropTypes.func,
};
